<template>
  <loading-spinner v-if="isLoading" small />
  <histogram-sparkline
    v-else-if="dataCategory === 'Continuous'"
    :key="'hsl-' + id + '-' + type + '-' + scaleId"
    :data="data"
    :width="width"
    :height="height"
    :responsive="responsive"
  />
  <column-chart
    v-else
    :data="data"
    :width="width"
    :height="height"
    :responsive="responsive"
  />
</template>

<script>
import { mapState } from 'vuex';
import { state } from '@/store/modules/datasetSummary/types';
import { histogram, extent } from 'd3';
import HistogramSparkline from '@/components/charts/HistogramSparkline.vue';
import ColumnChart from '@/components/charts/ColumnChart.vue';

export default {
  components: {
    HistogramSparkline,
    ColumnChart,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    scaleId: {
      type: Number,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    dataCategory: {
      type: String,
      required: true,
    },
    // Pre-reduced values, one per subject. When null (the default) every row
    // in the store is used, which is what the Variables tab wants. The Data
    // Dashboard supplies baseline values so each subject is counted once.
    values: {
      type: Array,
      default: null,
    },
    width: {
      type: Number,
      default: 100,
    },
    height: {
      type: Number,
      default: 25,
    },
    responsive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
      data: [],
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      isCollection: state.IS_COLLECTION,
      variableData: state.VARIABLE_DATA,
    }),
  },
  watch: {
    values() {
      this.render();
    },
  },
  created() {
    this.render();
  },
  methods: {
    render() {
      this.isLoading = true;

      const rawValues = this.getRawValues();

      if (this.dataCategory == 'Continuous') {
        // Bin data for histogram
        this.data = this.binVariableData(rawValues);
      } else {
        // Group and count
        this.data = this.groupVariableData(rawValues);
      }

      // Anything labelling these axes has to read the binning from here. A
      // caller deriving the peak or the level order for itself could disagree
      // with the bars actually on screen.
      this.$emit('axes', {
        peak: this.peakCount(),
        levels: this.levelLabels(),
      });
      this.isLoading = false;
    },
    peakCount() {
      const countOf =
        this.dataCategory == 'Continuous' ? d => d.length : d => d.count;
      return this.data.reduce((m, d) => Math.max(m, countOf(d)), 0);
    },
    // In bar order, which groupVariableData makes first-appearance order rather
    // than frequency order. Continuous bins have no labels to give.
    levelLabels() {
      if (this.dataCategory == 'Continuous') return [];
      return this.data.map(d => String(d.value));
    },
    getRawValues() {
      if (this.values !== null) {
        return this.values;
      }
      const bucket = this.variableData[this.type];
      const rows = bucket == null ? null : bucket[this.scaleId];
      return rows == null ? [] : rows.map(d => d['value']);
    },
    binVariableData(values) {
      const fcounts = values.map(v => +v);
      const ex = extent(fcounts);
      const bin_fn = histogram()
        .value(d => d)
        .domain(ex);
      return bin_fn(fcounts);
    },
    groupVariableData(values) {
      const fcounts = {};
      for (const v of values) {
        if (!fcounts[v]) {
          fcounts[v] = { value: v, count: 1 };
        } else {
          fcounts[v].count += 1;
        }
      }
      return Object.values(fcounts);
    },
  },
};
</script>

<style lang="scss" scoped></style>
